import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';

type TimelineRecord = {
  id: string;
  user_id: string | null;
  patient_id: string | null;
  patient_external_id: number;
  record_type: 'Note' | 'Call' | 'Email' | 'Deadline';
  body: string;
  record_date: string;
  staff_external_id: number | null;
  is_active: boolean | null;
  source: string | null;
  created_at: string;
  /** Outbound to 4D /timelineRecords (migration 20260414120000) */
  synced_to_4d_at?: string | null;
  sync_to_4d_error?: string | null;
  fourd_timeline_record_id?: number | null;
  patients?: { first_name?: string; last_name?: string } | null;
};

function syncStatusLabel(record: TimelineRecord): {
  label: string;
  detail: string;
  tone: 'ok' | 'err' | 'pending';
} {
  if (record.synced_to_4d_at) {
    const when = format(new Date(record.synced_to_4d_at), 'PP p');
    const id =
      record.fourd_timeline_record_id != null
        ? ` · 4D #${record.fourd_timeline_record_id}`
        : '';
    return { label: 'Synced', detail: `${when}${id}`, tone: 'ok' };
  }
  if (record.sync_to_4d_error) {
    return { label: 'Failed', detail: record.sync_to_4d_error, tone: 'err' };
  }
  if (record.source === '4d') {
    return {
      label: 'Inbound',
      detail: 'Received from 4D; no outbound step',
      tone: 'pending',
    };
  }
  return {
    label: 'Pending',
    detail: 'Not pushed to 4D yet',
    tone: 'pending',
  };
}

export default function TimelineRecords() {
  const [patientExternalId, setPatientExternalId] = React.useState('');
  const [recordType, setRecordType] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const { data: records, isLoading } = useQuery({
    queryKey: ['timeline-records', patientExternalId, recordType, startDate, endDate],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let query = supabase
        .from('timeline_records')
        .select('*, patients(first_name,last_name)')
        .order('record_date', { ascending: false });

      if (patientExternalId.trim()) {
        const patientIdNumber = Number(patientExternalId);
        if (!Number.isNaN(patientIdNumber)) {
          query = query.eq('patient_external_id', patientIdNumber);
        }
      }

      if (recordType) {
        query = query.eq('record_type', recordType);
      }

      if (startDate) {
        query = query.gte('record_date', new Date(startDate).toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte('record_date', endOfDay.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as TimelineRecord[];
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Timeline Records</h1>
        <p className="text-gray-600">4D EMR timeline notes, calls, and emails</p>
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Patient ID</label>
            <input
              type="text"
              value={patientExternalId}
              onChange={(e) => setPatientExternalId(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="External PatientId"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={recordType}
              onChange={(e) => setRecordType(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="Note">Note</option>
              <option value="Call">Call</option>
              <option value="Email">Email</option>
              <option value="Deadline">Deadline</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <div className="text-gray-500">Loading timeline records...</div>
        ) : records && records.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    4D sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Body
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => {
                  const patientName = record.patients
                    ? `${record.patients.first_name || ''} ${record.patients.last_name || ''}`.trim()
                    : '';
                  const sync = syncStatusLabel(record);
                  const syncColor =
                    sync.tone === 'ok'
                      ? 'text-green-800 bg-green-50'
                      : sync.tone === 'err'
                        ? 'text-red-800 bg-red-50'
                        : 'text-gray-600 bg-gray-100';
                  const detailShort =
                    sync.detail.length > 72 ? `${sync.detail.slice(0, 69)}…` : sync.detail;
                  return (
                    <tr key={record.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.record_date ? format(new Date(record.record_date), 'PP p') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.record_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patientName || `Patient ${record.patient_external_id}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.staff_external_id ?? '-'}
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs">
                        <span
                          className={`inline-flex flex-col gap-0.5 rounded-md px-2 py-1 text-xs font-medium ${syncColor}`}
                        >
                          <span>{sync.label}</span>
                          <span
                            className="font-normal text-[11px] leading-snug opacity-90"
                            title={sync.detail}
                          >
                            {detailShort}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xl truncate">
                        {record.body}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-500">No timeline records found.</div>
        )}
      </Card>
    </div>
  );
}
